import React, { useState } from "react";
import { Mail, Lock, Eye, EyeOff, CheckCircle, Smartphone, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

function LoginPage() {
  const [loginMethod, setLoginMethod] = useState("password"); 
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [otpData, setOtpData] = useState({
    phoneNumber: "",
    otp: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const API_BASE_URL = 'https://api.boomontrip.com';

  const normalizeUserData = (userData) => {
    if (!userData) return null;
    
    return {
      _id: userData._id || userData.id || '',
      name: userData.name || userData.fullName || userData.userName || '',
      email: userData.email || userData.emailAddress || userData.mail || '',
      phone: userData.phone || userData.phoneNumber || userData.mobile || userData.mobileNumber || userData.contactNumber || '',
      gender: userData.gender || userData.sex || '',
      dob: userData.dob || userData.dateOfBirth || userData.birthDate || '',
      hometown: userData.hometown || userData.city || userData.homeCity || '',
      alternativeNumber: userData.alternativeNumber || userData.alternateNumber || userData.altPhone || userData.alternatePhone || userData.secondaryPhone || '',
      profilePic: userData.profilePic || userData.profilePicture || userData.avatar || userData.photo || '',
      isVerified: userData.isVerified || userData.verified || false,
      createdAt: userData.createdAt || userData.created_at || userData.registeredAt || ''
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleOtpChange = (e) => {
    const { name, value } = e.target;
    setOtpData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const err = {};
    if (!formData.email) err.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      err.email = "Invalid email address";
    if (!formData.password) err.password = "Password is required";
    return err;
  };

  const validateOtpForm = () => {
    const err = {};
    if (!otpData.phoneNumber) {
      err.phoneNumber = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(otpData.phoneNumber.replace(/\s/g, ""))) {
      err.phoneNumber = "Invalid phone number";
    }
    if (otpSent && !otpData.otp) {
      err.otp = "OTP is required";
    }
    return err;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();

    const err = validateOtpForm();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    setSendingOtp(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: otpData.phoneNumber.replace(/\s/g, "")
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      setOtpSent(true);

      if (data.devOtp) {
        setErrors({ success: `` });
      } else {
        setErrors({ success: 'OTP sent successfully via WhatsApp!' });
      }

    } catch (error) {
      setErrors({ general: error.message || 'Failed to send OTP. Please try again.' });
    } finally {
      setSendingOtp(false);
    }
  };


  const handleOtpLogin = async (e) => {
    e.preventDefault();

    const err = validateOtpForm();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: otpData.phoneNumber.replace(/\s/g, ""),
          otp: otpData.otp
        }),
      });

      const data = await response.json();
      
      console.log('OTP Login Response:', data);

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Invalid OTP');
      }

      // Save token first
      const token = data.token;
      if (!token) {
        throw new Error('No token received from server');
      }
      
      localStorage.setItem('authToken', token);
      console.log('Token saved successfully');

      // Step 2: Fetch complete user profile using the token
      try {
        const profileResponse = await fetch(`${API_BASE_URL}/api/auth/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          console.log('Complete profile fetched:', profileData);
          
          // Handle different response formats
          let rawUserData = null;
          if (profileData.user) {
            rawUserData = profileData.user;
          } else if (profileData.data && profileData.data.user) {
            rawUserData = profileData.data.user;
          } else if (profileData.data) {
            rawUserData = profileData.data;
          } else {
            rawUserData = profileData;
          }

          // Normalize field names
          const userData = normalizeUserData(rawUserData);
          localStorage.setItem('userData', JSON.stringify(userData));
          console.log('Normalized user data saved:', userData);
        } else {
          // If profile fetch fails, save whatever data we got from OTP verification
          console.warn('Profile fetch failed, using OTP response data');
          let rawUserData = null;
          if (data.user) {
            rawUserData = data.user;
          } else if (data.data && data.data.user) {
            rawUserData = data.data.user;
          } else if (data.data) {
            rawUserData = data.data;
          }

          if (rawUserData) {
            const userData = normalizeUserData(rawUserData);
            localStorage.setItem('userData', JSON.stringify(userData));
            console.log('Fallback normalized user data saved:', userData);
          }
        }
      } catch (profileError) {
        console.error('Profile fetch error:', profileError);
        // Continue with login even if profile fetch fails
      }

      // Show success
      setSuccess(true);

      // Redirect after 1.5 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);

    } catch (error) {
      console.error('OTP Login Error:', error);
      setErrors({ general: error.message || 'OTP verification failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };


  // Email/Password Login
  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validateForm();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      console.log('Email Login Response:', data); // Debug log

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Login failed');
      }

      // Save token
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        console.log('Token saved:', data.token);
      }

      // Save user data - normalize field names
      if (data.user) {
        const userData = normalizeUserData(data.user);
        localStorage.setItem('userData', JSON.stringify(userData));
        console.log('Normalized user data saved:', userData);
      }

      // Show success
      setSuccess(true);

      // Redirect after 1.5 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);

    } catch (error) {
      console.error('Email Login Error:', error);
      setErrors({ general: error.message || 'Login failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-6 sm:p-8 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={window.innerWidth < 640 ? 40 : 48} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Login Successful!
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            Redirecting to homepage...
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-green-600 h-2 rounded-full animate-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 sm:p-8">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2">Welcome Back</h2>
          <p className="text-center text-gray-600 text-xs sm:text-sm">
            Login to continue to BoomOnTrip
          </p>
        </div>

        {/* Login Method Toggle */}
        <div className="flex gap-1 sm:gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            onClick={() => {
              setLoginMethod("password");
              setErrors({});
              setOtpSent(false);
            }}
            className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-all ${
              loginMethod === "password"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Mail className="inline mr-1 sm:mr-2" size={16} />
            <span className="hidden xs:inline">Email/Password</span>
            <span className="xs:hidden">Email</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginMethod("otp");
              setErrors({});
            }}
            className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-all ${
              loginMethod === "otp"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Smartphone className="inline mr-1 sm:mr-2" size={16} />
            <span className="hidden xs:inline">OTP Login</span>
            <span className="xs:hidden">OTP</span>
          </button>
        </div>

        {/* Error/Success Messages */}
        {errors.general && (
          <div className="mb-4 text-xs sm:text-sm bg-red-50 text-red-600 px-3 sm:px-4 py-2 sm:py-3 rounded-lg">
            {errors.general}
          </div>
        )}

      

        {/* EMAIL/PASSWORD LOGIN FORM */}
        {loginMethod === "password" && (
          <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
            {/* EMAIL */}
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700">Email Address</label>
              <div className="relative mt-1">
                <Mail
                  className="absolute left-3 top-2.5 sm:top-3 text-gray-400"
                  size={16}
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={`w-full pl-9 sm:pl-10 py-2 sm:py-2.5 text-sm sm:text-base border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${
                    errors.email ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* PASSWORD */}
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700">Password</label>
              <div className="relative mt-1">
                <Lock
                  className="absolute left-3 top-2.5 sm:top-3 text-gray-400"
                  size={16}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  className={`w-full pl-9 sm:pl-10 pr-9 sm:pr-10 py-2 sm:py-2.5 text-sm sm:text-base border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${
                    errors.password ? "border-red-400" : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 sm:top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password}</p>
              )}
            </div>

            {/* FORGOT PASSWORD */}
            <div className="text-right">
              <Link
                to="/forget"
                className="text-xs sm:text-sm text-blue-600 hover:underline"
              >
                Forgot Password?
              </Link>
            </div>

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 rounded-md text-sm sm:text-base font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        {/* OTP LOGIN FORM */}
        {loginMethod === "otp" && (
          <form className="space-y-4 sm:space-y-5" onSubmit={otpSent ? handleOtpLogin : handleSendOtp}>
            {/* PHONE NUMBER */}
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700">Phone Number</label>
              <div className="relative mt-1">
                <Smartphone
                  className="absolute left-3 top-2.5 sm:top-3 text-gray-400"
                  size={16}
                />
                <input
                  type="tel"
                  name="phoneNumber"
                  value={otpData.phoneNumber}
                  onChange={handleOtpChange}
                  placeholder="10-digit mobile number"
                  disabled={otpSent}
                  className={`w-full pl-9 sm:pl-10 py-2 sm:py-2.5 text-sm sm:text-base border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${
                    errors.phoneNumber ? "border-red-400" : "border-gray-300"
                  } ${otpSent ? "bg-gray-50" : ""}`}
                />
              </div>
              {errors.phoneNumber && (
                <p className="text-xs text-red-500 mt-1">{errors.phoneNumber}</p>
              )}
              {otpSent && (
                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtpData({ ...otpData, otp: "" });
                    setErrors({});
                  }}
                  className="text-xs text-blue-600 hover:underline mt-1 flex items-center"
                >
                  <ArrowLeft size={12} className="mr-1" />
                  Change number
                </button>
              )}
            </div>

            {/* OTP INPUT (shown after OTP is sent) */}
            {otpSent && (
              <div>
                <label className="text-xs sm:text-sm font-medium text-gray-700">Enter OTP</label>
                <div className="relative mt-1">
                  <Lock
                    className="absolute left-3 top-2.5 sm:top-3 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    name="otp"
                    value={otpData.otp}
                    onChange={handleOtpChange}
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`w-full pl-9 sm:pl-10 py-2 sm:py-2.5 text-sm sm:text-base border rounded-md focus:ring-2 focus:ring-blue-500 outline-none tracking-widest ${
                      errors.otp ? "border-red-400" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.otp && (
                  <p className="text-xs text-red-500 mt-1">{errors.otp}</p>
                )}
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp}
                  className="text-xs text-blue-600 hover:underline mt-1 disabled:opacity-50"
                >
                  {sendingOtp ? "Resending..." : "Resend OTP"}
                </button>
              </div>
            )}

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              disabled={loading || sendingOtp}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 rounded-md text-sm sm:text-base font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingOtp
                ? "Sending OTP..."
                : otpSent
                  ? loading
                    ? "Verifying..."
                    : "Verify & Login"
                  : "Send OTP"}
            </button>

            <p className="text-xs text-gray-500 text-center leading-relaxed">
              OTP will be sent to your mobile number via WhatsApp
            </p>
          </form>
        )}

        {/* Sign Up Link */}
        <p className="text-center text-xs sm:text-sm mt-6">
          Don't have an account?{" "}
          <Link to="/signup" className="text-blue-600 font-medium hover:underline">
            Sign up
          </Link>
        </p>
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress 1.5s ease-out;
        }
        
        /* Custom breakpoint for very small phones */
        @media (min-width: 375px) {
          .xs\\:inline {
            display: inline;
          }
          .xs\\:hidden {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

export default LoginPage;